/**
 * Phase 3A - the contractual escalation engine.
 *
 * Turns the lease-level rent steps Phase 2 extracts into a year-by-year contractual
 * RENT schedule, modelled monthly so a bump lands on its real effective date.
 *
 * This is what kills two of the reference guide's pitfalls:
 *
 *   Pitfall 2 - generic annual growth. The schedule comes from the actual lease terms,
 *               so Bear Valley is FLAT for years 1-3 rather than drifting up at 3%.
 *   Pitfall 3 - partial-year blending. An October bump gives 9 months at the old rate
 *               and 3 at the new one, because that is the rent actually collected.
 *
 * Joseph's ruling 2026-08-01 (Option A): blend from the REAL effective dates even though
 * that does not reproduce the delivered PDF, which used a half-year convention for
 * AutoZone and no blend at all for Dollar Tree's November bump - overstating NOI by about
 * $11,800 across the hold, in the direction that flatters the deal.
 *
 * IMPORTANT - this returns RENT, not NOI. The caller subtracts its own modelled operating
 * expenses to reach NOI. Deriving expenses here would be a second expense model, which is
 * exactly the defect class that has dominated this build.
 *
 * Renewal, downtime, market rent at rollover and TI/LC are Phase 3B. Where this engine
 * cannot know the contractual rent it returns `null` for that year rather than guessing,
 * which `NoiSchedule` explicitly supports ("no contractual figure, model it").
 */

export interface ScheduleEscalation {
  effective_date?: string;
  escalation_type?: 'percent' | 'dollar_psf' | 'fixed_amount' | 'cpi';
  escalation_value?: number | string;
  new_annual_rent?: number | string;
  new_rent_per_sf?: number | string;
  /**
   * F2. Set when the lease escalates on the SAME schedule repeatedly rather than in one-off
   * steps - "2% annually through the primary term". The extractor recorded exactly that as a
   * single step plus a sentence on 2 of 5 deals measured, so their schedules ran flat and cost
   * up to 2.61 points of IRR.
   * ⛔ It expands only what the lease STATES. Absent this field nothing is generated, because
   * reading a rule out of a prose note would be guessing.
   */
  recurrence?: 'annual' | null;
  /** Last date the recurring rule applies. Defaults to the tenant's lease expiry. */
  recurrence_through?: string | null;
  /**
   * Whether `effective_date` came from the DOCUMENT or was inferred.
   *
   * 🚨 Bank of the Sierra's OM gives "2.25% annual escalations" and NO dates. Two extractions of
   * it produced two different first-step dates (2028-01-01, then 2027-01-01), and neither used
   * the 12/21 anniversary its commencement implies. Nothing regressed - the document does not
   * contain the answer, so the field was never grounded and a prompt change perturbed a guess.
   * ⭐ **Any field the OM does not state is unstable across extractions.** Marked 'inferred', a
   * recurring rule is snapped to the lease anniversary and the schedule SAYS so.
   */
  date_basis?: 'stated' | 'inferred' | null;
}

/**
 * Numeric fields are `string | number` on purpose. The extracted rent roll round-trips
 * through jsonb and comes back with numbers as STRINGS - the saved Bear Valley property
 * carries `"monthly_base_rent": "11900"`. A `typeof v === 'number'` guard silently
 * scores those tenants at zero rent, which no unit test using numeric fixtures would
 * ever catch.
 */
export interface ScheduleTenant {
  tenant_name?: string;
  leased_sf?: number | string;
  monthly_base_rent?: number | string;
  rent_per_sf?: number | string;
  lease_start?: string;
  lease_end?: string;
  escalations?: ScheduleEscalation[] | null;
}

export interface ScheduleYear {
  year: number;
  rent: number | null;
  notes: string[];
  /**
   * Tenants out of term for part or all of this year whose rent is therefore NOT in `rent`.
   * ⭐ F3, Joseph's ruling 2026-08-27: the year is a FLOOR and it says whose rent is missing,
   * rather than being discarded wholesale because one tenant rolled.
   */
  unmodelled?: string[];
}

/**
 * Phase 3B rollover assumptions. Applied to any lease expiring inside the hold.
 *
 * The rollover is modelled as an EXPECTED VALUE, not a coin flip resolved one way:
 * every month after expiry earns `p x renewal outcome + (1-p) x re-let outcome`.
 * Pretending renewal is certain is the optimistic error; pretending it never happens is
 * the pessimistic one. Neither is a forecast a broker should defend.
 *
 * Downtime and free rent apply ONLY to the re-let branch - a renewing tenant never
 * vacates and is not given a concession here.
 */
export interface RolloverAssumptions {
  /** 0-100. */
  renewalProbability: number;
  /** Vacant months before a new tenant takes occupancy. Re-let branch only. */
  downtimeMonths: number;
  /** Annual $/SF the space re-lets at. */
  marketRentPerSf: number;
  /** Concession months at zero rent after downtime. Re-let branch only. */
  freeRentMonths: number;
  /** What a renewing tenant pays. Defaults to market. */
  renewalRentBasis?: 'market' | 'flat';
  /** Capital, reported separately from rent - TI/LC is capex, never an operating expense. */
  tiNewPerSf?: number;
  tiRenewalPerSf?: number;
  lcNewPercent?: number;
  lcRenewalPercent?: number;
}

export interface RentScheduleResult {
  /** Index 0 = year 1. `null` means "not contractually known, model it". */
  annualRent: Array<number | null>;
  detail: ScheduleYear[];
  warnings: string[];
  /**
   * TI and LC by year, index 0 = year 1. Capital, NOT netted against rent - folding
   * leasing capital into NOI would overstate the cap rate at exit.
   */
  capitalCosts: number[];
}

const MS = 1;

/** Coerces the string-or-number the rent roll actually carries. Returns null when unusable. */
const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const parsed = Number(v.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** Parsed as UTC midday so a timezone offset can never roll a date into the previous day. */
const parseDate = (s?: string | null): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime()) ? null : d;
};

const toIso = (d: Date): string => d.toISOString().slice(0, 10);

const addMonths = (d: Date, n: number): Date => {
  const out = new Date(d.getTime());
  out.setUTCMonth(out.getUTCMonth() + n);
  return out;
};

/**
 * ⭐⭐ F2 - MATERIALISING A STATED RECURRING ESCALATION.
 *
 * "2% annually through the term" is ONE sentence in an OM and TWENTY steps in a proforma. The
 * extraction prompt already said "Do NOT summarise rent steps as a growth rate. For every
 * contractual bump, record a separate entry" - and the model summarised anyway on 2 of 5 deals
 * measured 2026-08-27. ⭐⭐ **Prompt is intent; the guard is enforcement.** So the recurrence is
 * captured as a structured field and expanded HERE, deterministically, where it can be tested.
 *
 * ⛔ THREE THINGS IT WILL NOT DO. It will not expand without an explicit `recurrence`; it will
 * not run past the lease expiry; and it will not overwrite a step the document stated itself -
 * the OM's own figure always wins over our compounding, which is what keeps STIIIZY's 21
 * explicit steps exact.
 */
const expandRecurring = (
  t: ScheduleTenant,
  /**
   * The day `monthly_base_rent` is CURRENT AS OF - the day the document speaks for.
   * ⛔ NOT the day being queried. The anchor is fixed once from the document's own statement;
   * deriving it from the query date would re-anchor the series every time it is read, so asking
   * for the rent in 2030 would push the first increase past 2030.
   * ⛔ Omitted means the caller does not know, and the year-anchoring rule below DOES NOT FIRE.
   */
  baseRentAsOf?: Date | null
): ScheduleEscalation[] => {
  const given = (t.escalations ?? []).filter(e => e && e.effective_date);
  const recurring = given.filter(e => e.recurrence === 'annual');
  if (!recurring.length) return given;

  const explicitDates = new Set(given.map(e => (e.effective_date ?? '').slice(0, 10)));
  const sf = num(t.leased_sf);
  const leaseEnd = parseDate(t.lease_end);
  const out: ScheduleEscalation[] = [...given];

  for (const rule of recurring) {
    let from = parseDate(rule.effective_date);
    if (!from) continue;

    /**
     * ⭐⭐ AN INFERRED DATE IS DERIVED, NOT GUESSED. A rent that escalates annually escalates on
     * its lease anniversary unless the document says otherwise - so where the extractor had to
     * invent the date, the anniversary of `lease_start` replaces it. ⛔ A STATED date is left
     * alone: the document outranks the rule, which is what keeps STIIIZY's 22 printed steps exact.
     */
    const commencement = parseDate(t.lease_start);
    if (rule.date_basis === 'inferred' && commencement) {
      // The most recent anniversary on or before the date the extractor guessed.
      let anchored = new Date(Date.UTC(
        from.getUTCFullYear(), commencement.getUTCMonth(), commencement.getUTCDate(), 12));
      if (anchored.getTime() > from.getTime()) anchored = addMonths(anchored, -12);

      /**
       * ⭐ A RESTATED CURRENT RENT MARKS AN ANNIVERSARY THAT HAS ALREADY PASSED. When the entry's
       * `new_annual_rent` is simply the rent already running - Sierra's $150,408 is exactly
       * `monthly_base_rent x 12` - it is not a step, it is the extractor saying "the series is
       * anchored here". The rent it names therefore began at the PREVIOUS anniversary, and the
       * first real increase is the one after that.
       * ⚠️ Without this the whole schedule sits a year late and his year 2 never rises.
       */
      const base = (num(t.monthly_base_rent) ?? 0) * 12;
      const restatesCurrent = base > 0 && Math.abs((num(rule.new_annual_rent) ?? 0) - base) < 1;
      if (restatesCurrent) anchored = addMonths(anchored, -12);

      /**
       * ⭐⭐ THE ANNIVERSARY FIXES THE MONTH AND DAY. THIS FIXES THE YEAR.
       *
       * 🚨 The Station, 2181-2191 Lakewood Blvd, extracted 2026-08-30: an inferred first step on
       * 2026-07-01 - sixty days in the PAST - lifting a rent the same extraction annotated
       * "Current annual rent $238,896" to $246,063. That figure matches no reading of the OM:
       * $238,896 if the stated rent is current, $261,050 if it is the Jul 2023 starting rate with
       * all three anniversaries run. One derived step on top of a rent labelled current is a
       * DOUBLE COUNT, and it moved the going-in cap 0.13pp off the delivered analysis.
       *
       * ⛔ AN INCREASE CANNOT PREDATE THE DAY THE DOCUMENT SAYS THE BASE RENT IS IN FORCE.
       * So a first occurrence that names a DIFFERENT rent is walked forward whole anniversaries
       * until it lands after that day. The series is moved, never deleted - the next real
       * increase still arrives, one anniversary later.
       *
       * ⛔ NOT when the entry RESTATES the current rent. There the entry is an anchor marker
       * rather than an increase, the rate it produces already equals the stated base, and the
       * step BACK above is deliberate. Applying this to Bank of the Sierra would push its real
       * increase a year late and break a deal that reconciles today.
       */
      if (baseRentAsOf && !restatesCurrent) {
        for (let guard = 0; guard < 60 && anchored.getTime() <= baseRentAsOf.getTime(); guard++) {
          anchored = addMonths(anchored, 12);
        }
      }

      from = anchored;
    }
    const through = parseDate(rule.recurrence_through) ?? leaseEnd;
    if (!through) continue;                       // no horizon - refuse to run forever
    const type = rule.escalation_type;
    const value = num(rule.escalation_value);
    if (value === null || type === 'cpi') continue;   // CPI stays unknowable

    // The FIRST occurrence keeps the document's own figure; only later ones are derived.
    let rent = num(rule.new_annual_rent);
    if (rent === null) continue;

    // When the date moved, the original entry still sits in `out` on its guessed date - replace it.
    if (from.getTime() !== (parseDate(rule.effective_date) as Date).getTime()) {
      const i = out.indexOf(rule);
      if (i >= 0) out[i] = { ...rule, effective_date: toIso(from) };
    }

    for (let i = 1; i <= 60; i++) {               // 60 is a horizon, not a limit anyone reaches
      const on = addMonths(from, i * 12);
      if (on.getTime() > through.getTime()) break;
      if (type === 'percent') rent = rent * (1 + value / 100);
      else if (type === 'dollar_psf' && sf !== null) rent = rent + value * sf;
      else break;                                 // fixed_amount does not repeat meaningfully

      const iso = toIso(on);
      if (explicitDates.has(iso)) continue;       // ⛔ the document's own step wins
      out.push({
        effective_date: iso,
        escalation_type: type,
        escalation_value: value,
        new_annual_rent: rent,
        // A derived occurrence of an inferred rule is itself inferred - otherwise the lease-year
        // table prints a computed anniversary as though the document had quoted it.
        date_basis: rule.date_basis,
      });
    }
  }
  return out;
};

/**
 * ⭐⭐ THE STATED OPTION RENT IN FORCE ON `when`, for a tenant past its lease end.
 *
 * 🚨 Stagecoach Plaza states every option rent - Harbor Freight's five, AutoZone's three, Dollar
 * Tree's, Doraval's - and the extractor captured all of them at their stated dates. Every one is
 * dated the day AFTER its lease expires, so `annualRentAt` dropped the tenant as out of term and
 * the schedule refused years 2 to 5 outright, asking instead for ONE deal-wide market rent.
 * **Four documented figures replaced by a single guess.**
 *
 * ⭐ AN OPTION RENT AND A MARKET RENT ANSWER DIFFERENT QUESTIONS. The option is what a RENEWING
 * tenant pays and the lease states it; the market rate is what a REPLACEMENT tenant pays and
 * nobody has signed it. `renewalRentBasis: 'market'` used the market figure for both.
 *
 * ⛔ STATED ONLY. An `inferred` date is the extractor guessing, and a guess must not outrank the
 * assumption a human deliberately entered. ⛔ And only steps dated PAST the expiry: a bump inside
 * the term is a contractual escalation, not a renewal rent.
 */
const statedOptionRentAt = (t: ScheduleTenant, when: Date): number | null => {
  const le = parseDate(t.lease_end);
  if (!le) return null;

  const options = (t.escalations ?? [])
    .filter(e => e && e.date_basis !== 'inferred' && e.effective_date)
    .map(e => ({ e, on: parseDate(e.effective_date) }))
    .filter((x): x is { e: ScheduleEscalation; on: Date } => x.on !== null && x.on.getTime() > le.getTime())
    .sort((a, b) => a.on.getTime() - b.on.getTime());
  if (options.length === 0) return null;

  const sf = num(t.leased_sf);
  let rent: number | null = null;
  for (const { e, on } of options) {
    if (on.getTime() > when.getTime()) break;
    const newAnnual = num(e.new_annual_rent);
    const val = num(e.escalation_value);
    const newPsf = num(e.new_rent_per_sf);
    if (newAnnual !== null) rent = newAnnual;
    else if (newPsf !== null && sf !== null) rent = newPsf * sf;
    else if (e.escalation_type === 'percent' && val !== null && rent !== null) rent = rent * (1 + val / 100);
    else if (e.escalation_type === 'fixed_amount' && val !== null) rent = val;
    // ⛔ CPI or an underivable step: keep the last known option rent rather than inventing one.
    else break;
  }
  return rent;
};

/**
 * The annual rent a tenant is contractually on as of `when`.
 * Returns null when an escalation applies but its amount cannot be derived (CPI).
 */
const annualRentAt = (
  t: ScheduleTenant,
  when: Date,
  baseRentAsOf?: Date | null
): { rent: number; note?: string } | null => {
  const monthly = num(t.monthly_base_rent);
  const sf = num(t.leased_sf);
  const base = monthly !== null ? monthly * 12 : 0;

  const steps = expandRecurring(t, baseRentAsOf)
    .map(e => ({ e, on: parseDate(e.effective_date) }))
    .filter((x): x is { e: ScheduleEscalation; on: Date } => x.on !== null)
    .sort((a, b) => a.on.getTime() - b.on.getTime());

  let rent = base;
  let note: string | undefined;

  for (const { e, on } of steps) {
    if (on.getTime() > when.getTime()) break;

    const newAnnual = num(e.new_annual_rent);
    const val = num(e.escalation_value);
    const newPsf = num(e.new_rent_per_sf);

    if (newAnnual !== null) {
      rent = newAnnual;
    } else if (e.escalation_type === 'percent' && val !== null) {
      rent = rent * (1 + val / 100);
    } else if (e.escalation_type === 'dollar_psf' && val !== null && sf !== null) {
      rent = rent + val * sf;
    } else if (e.escalation_type === 'fixed_amount' && val !== null) {
      rent = val;
    } else if (newPsf !== null && sf !== null) {
      rent = newPsf * sf;
    } else {
      // CPI, or a step with no derivable amount. Refuse to invent one.
      return null;
    }
    note = `${t.tenant_name ?? 'Tenant'} ${
      e.escalation_type === 'percent' ? `${e.escalation_value}%`
      : e.escalation_type === 'dollar_psf' ? `$${e.escalation_value}/SF`
      : 'step'
    } eff ${e.effective_date}`;
  }

  return { rent, note };
};

export const buildRentSchedule = (input: {
  tenants: ScheduleTenant[];
  holdYears: number;
  /** Close date. Year 1 runs from here, per Joseph's ruling. Defaults to Jan 1. */
  startDate?: string;
  /** Phase 3B. Without this the engine refuses to guess past a lease expiry. */
  rollover?: RolloverAssumptions;
  /**
   * The day `monthly_base_rent` is current as of - today on the analyzer screen, NOT the close.
   * ⛔ Omitted leaves an inferred series exactly where the extractor put it. See `expandRecurring`.
   */
  baseRentAsOf?: string;
}): RentScheduleResult => {
  const { tenants, holdYears, rollover } = input;
  const start = parseDate(input.startDate) ?? parseDate(`${new Date().getUTCFullYear()}-01-01`)!;
  const baseAsOf = parseDate(input.baseRentAsOf);

  const warnings: string[] = [];
  const detail: ScheduleYear[] = [];
  const annualRent: Array<number | null> = [];

  if (!tenants.length) {
    warnings.push('No tenant rent roll on this property, so no contractual schedule can be built.');
    for (let y = 1; y <= holdYears; y++) {
      annualRent.push(null);
      detail.push({ year: y, rent: null, notes: ['No tenants'] });
    }
    return { annualRent, detail, warnings, capitalCosts: new Array(holdYears).fill(0) };
  }

  /**
   * ⭐⭐ F3 - PER TENANT, NOT PER YEAR. Joseph's ruling 2026-08-27.
   *
   * A lease ending inside the hold means ITS rent afterwards depends on renewal terms this engine
   * deliberately does not model. That refusal is right and it stays. What changed is its
   * GRANULARITY: it used to null the entire year for EVERY tenant, so on Mojave River Academy a
   * Wells Fargo ATM pad going dark in 2029 discarded the anchor school tenant's known 3%
   * contractual steps and the schedule read 218,217 / 223,827 / then flat - it rose and fell back
   * below where it started, costing ~2 points of IRR.
   *
   * ⛔ On a multi-tenant deal a lease rolling inside the hold is the COMMON case. Now each year
   * carries the contractual rent of the tenants actually in term, and NAMES the ones it is
   * missing, so the figure is an explicit floor rather than a silent guess.
   */
  let firstExpiryYear: number | null = null;
  for (const t of tenants) {
    const end = parseDate(t.lease_end);
    if (!end) continue;
    for (let y = 1; y <= holdYears; y++) {
      const yEnd = addMonths(start, y * 12);
      if (end.getTime() < yEnd.getTime() - MS) {
        if (firstExpiryYear === null || y < firstExpiryYear) firstExpiryYear = y;
        break;
      }
    }
  }
  /**
   * ⭐⭐ F2, THE ENFORCEMENT HALF. The engine cannot know whether an OM stated a recurring rule,
   * and it will not read one out of a prose note - that would be guessing. But ONE recorded step
   * against a long remaining term is the exact signature of the extraction gap that cost Kiddie
   * Academy 2.61 points of IRR and Bank of the Sierra 1.33, and saying so costs nothing.
   *
   * ⛔ Silence here is what made the defect invisible: a flat schedule looks like a flat lease.
   */
  for (const t of tenants) {
    const steps = (t.escalations ?? []).filter(e => e && e.effective_date);
    if (steps.length !== 1) continue;
    if (steps[0].recurrence) continue;                  // the rule is stated - nothing is missing
    const end = parseDate(t.lease_end);
    const last = parseDate(steps[0].effective_date);
    if (!end || !last) continue;
    const monthsLeft = (end.getTime() - last.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
    if (monthsLeft < 36) continue;                      // a short tail genuinely may hold one step
    warnings.push(
      `${t.tenant_name ?? 'Tenant'} has only one recorded rent step against ${Math.round(monthsLeft / 12)} more years of term. If the lease escalates every year, the schedule below is FLAT where it should rise - check the OM's rent schedule.`
    );
  }

  for (const t of tenants) {
    const inferred = (t.escalations ?? []).filter(e => e && e.date_basis === 'inferred');
    if (!inferred.length) continue;
    const anchor = t.lease_start;
    warnings.push(
      anchor
        ? `${t.tenant_name ?? 'Tenant'}'s escalation DATES are not stated in the document - only the rate. They are derived from the lease commencement anniversary (${anchor}). Confirm against the lease before relying on the year-by-year figures.`
        : `${t.tenant_name ?? 'Tenant'}'s escalation dates are not stated in the document and there is no lease commencement to derive them from, so the dates below are the extractor's estimate.`
    );
  }

  const capitalCosts: number[] = new Array(holdYears).fill(0);

  if (firstExpiryYear !== null && !rollover) {
    const rolling = tenants
      .filter(t => {
        const end = parseDate(t.lease_end);
        return end !== null && end.getTime() < addMonths(start, holdYears * 12).getTime();
      })
      .map(t => t.tenant_name ?? 'Tenant');
    warnings.push(
      `${rolling.join(', ')} ${rolling.length === 1 ? 'expires' : 'expire'} inside the hold. Those years show the contractual rent of the remaining leases only - a FLOOR that does not include ${rolling.length === 1 ? 'that tenant' : 'those tenants'}. Set rollover assumptions to model the renewal.`
    );
  } else if (firstExpiryYear !== null && rollover) {
    warnings.push(
      `A lease expires in year ${firstExpiryYear}. Rent after it is an EXPECTED VALUE at ${rollover.renewalProbability}% renewal, ${rollover.downtimeMonths} months downtime and $${rollover.marketRentPerSf}/SF market rent - an assumption, not a contract.`
    );
  }

  for (let y = 1; y <= holdYears; y++) {
    let total = 0;
    const notes: string[] = [];
    const unmodelled: string[] = [];
    let unknown = false;
    let anyInTerm = false;

    /**
     * 🚨🚨 A STUB YEAR IS NOT A RUN RATE. A lease ending PART WAY THROUGH this year prorates the
     * total below every run rate on either side of it, and the exit prices on the FINAL HOLD
     * YEAR - so a five-month stub landing there halved a sale and cost 14 points of IRR on
     * Mojave River Academy. Introduced by F3 and caught by re-measuring, not by a test.
     *
     * ⭐ The distinction is "is this a WHOLE year", not "did a tenant expire". A tenant absent
     * for the entire year leaves a true run rate for whoever remains - that one is kept and
     * named, which is exactly what F3 is for. Only the year they LEAVE IN is unknown.
     */
    const yStart = addMonths(start, (y - 1) * 12);
    const yEnd = addMonths(start, y * 12);
    const leavesMidYear = !rollover && tenants.some(t => {
      const le = parseDate(t.lease_end);
      if (!le) return false;
      // Ends on or after the first month of the year and before its last - i.e. inside it.
      return le.getTime() >= yStart.getTime() && le.getTime() < addMonths(yEnd, -1).getTime();
    });

    for (let m = 0; m < 12; m++) {
      const monthStart = addMonths(start, (y - 1) * 12 + m);

      for (const t of tenants) {
        const ls = parseDate(t.lease_start);
        const le = parseDate(t.lease_end);
        // A month counts only when the lease is actually in term for it.
        if (ls && monthStart.getTime() < ls.getTime()) continue;

        if (le && monthStart.getTime() > le.getTime()) {
          if (!rollover) {
            // ⭐ F3: named rather than nulled. The year becomes a floor that says what is missing.
            const who = t.tenant_name ?? 'Tenant';
            if (!unmodelled.includes(who)) unmodelled.push(who);
            continue;
          }
          // The tenant IS being modelled this month, so the year has contractual content.
          anyInTerm = true;
          const sf = num(t.leased_sf) ?? 0;
          const marketAnnual = rollover.marketRentPerSf * sf;
          const p = Math.min(100, Math.max(0, rollover.renewalProbability)) / 100;

          // Months elapsed since the lease ended, 0-based for the first vacant month.
          const monthsSince = Math.round(
            (monthStart.getTime() - addMonths(le, 0).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
          );

          /**
           * ⭐⭐ THE DOCUMENT OUTRANKS THE ASSUMPTION. When the lease STATES what a renewing
           * tenant pays, that is the renewal rent - not a deal-wide market guess. The market rate
           * still governs the RE-LET branch below, which is a different tenant entirely.
           */
          const statedOption = statedOptionRentAt(t, monthStart);
          const renewAnnual = statedOption ?? (rollover.renewalRentBasis === 'flat'
            ? (annualRentAt(t, le, baseAsOf)?.rent ?? marketAnnual)
            : marketAnnual);

          // `monthsSince` is 0 for the FIRST vacant month, so after `darkMonths` dark
          // months the paying month is index `darkMonths`, not `darkMonths + 1`.
          const darkMonths = (rollover.downtimeMonths ?? 0) + (rollover.freeRentMonths ?? 0);
          const reletAnnual = monthsSince >= darkMonths ? marketAnnual : 0;

          total += (p * renewAnnual + (1 - p) * reletAnnual) / 12;
          const label = statedOption !== null
            ? `${t.tenant_name ?? 'Tenant'} renews ${t.lease_end} at its stated option rent ` +
              `($${Math.round(statedOption).toLocaleString('en-US')}), ${rollover.renewalProbability}% renewal`
            : `${t.tenant_name ?? 'Tenant'} rolls over ${t.lease_end} at ${rollover.renewalProbability}% renewal`;
          if (!notes.includes(label)) notes.push(label);
          continue;
        }

        anyInTerm = true;
        const at = annualRentAt(t, monthStart, baseAsOf);
        if (at === null) {
          unknown = true;
          if (!notes.some(n => n.includes(t.tenant_name ?? 'Tenant'))) {
            notes.push(`${t.tenant_name ?? 'Tenant'} has a CPI or underivable step - no contractual figure`);
          }
          continue;
        }
        total += at.rent / 12;
        if (at.note && !notes.includes(at.note)) notes.push(at.note);
      }
    }

    // Leasing capital lands in the year the lease expires.
    if (rollover) {
      for (const t of tenants) {
        const le = parseDate(t.lease_end);
        if (!le) continue;
        const yStart = addMonths(start, (y - 1) * 12);
        const yEnd = addMonths(start, y * 12);
        if (le.getTime() < yStart.getTime() || le.getTime() >= yEnd.getTime()) continue;
        const sf = num(t.leased_sf) ?? 0;
        const marketAnnual = rollover.marketRentPerSf * sf;
        const p = Math.min(100, Math.max(0, rollover.renewalProbability)) / 100;
        const ti = p * (rollover.tiRenewalPerSf ?? 0) * sf + (1 - p) * (rollover.tiNewPerSf ?? 0) * sf;
        const lc = marketAnnual * (p * ((rollover.lcRenewalPercent ?? 0) / 100)
                                 + (1 - p) * ((rollover.lcNewPercent ?? 0) / 100));
        capitalCosts[y - 1] += ti + lc;
      }
    }

    if (unknown) {
      warnings.push(
        `Year ${y} contains a CPI or underivable escalation, so it has no contractual figure and is left to the growth model.`
      );
      annualRent.push(null);
      detail.push({ year: y, rent: null, notes, unmodelled });
      continue;
    }

    if (leavesMidYear) {
      const who = tenants
        .filter(t => {
          const le = parseDate(t.lease_end);
          return le !== null && le.getTime() >= yStart.getTime() && le.getTime() < addMonths(yEnd, -1).getTime();
        })
        .map(t => t.tenant_name ?? 'Tenant');
      notes.push(`${who.join(', ')} ${who.length === 1 ? 'ends' : 'end'} part way through this year, so it is not a full year of rent and carries no contractual figure.`);
      annualRent.push(null);
      detail.push({ year: y, rent: null, notes, unmodelled });
      continue;
    }

    // ⛔ Nobody in term for any month of the year. A floor of ZERO would be a CLAIM - "this
    // property earns nothing" - and layering it onto the in-place NOI produces a collapse that
    // looks modelled. Null is what it actually is: unknown, model it.
    if (!anyInTerm) {
      annualRent.push(null);
      detail.push({ year: y, rent: null, notes, unmodelled });
      continue;
    }

    annualRent.push(total);
    detail.push({
      year: y,
      rent: total,
      notes: notes.length ? notes : ['Flat - no contractual step this year'],
      unmodelled,
    });
  }

  return { annualRent, detail, warnings, capitalCosts };
};

export interface AnnualRentRateResult {
  /**
   * Total contractual annual rent RATE across every tenant in term on `asOf`.
   * `null` means it could not be derived, and the caller must refuse rather than model.
   */
  rate: number | null;
  notes: string[];
  warnings: string[];
}

/**
 * ⭐⭐ THE ANCHOR. The annual rent RATE in force on ONE DAY - never a year of collections.
 *
 * `buildRentSchedule` answers "what rent is collected over hold year y", which BLENDS across a
 * step landing mid-year. That is the right answer for a proforma year and the WRONG one for an
 * anchor, because the screen's in-place NOI is struck on a single day's rate. Anchoring a
 * schedule on a blended year mismatches the two by part of an escalation.
 *
 * ⛔ Measured on STIIIZY El Centro, 2026-08-27: this returns $386,355, exactly the in-place NOI
 * the property carries. A one-year schedule from the same day returns $392,553 - it has already
 * absorbed part of the 1 January step. That difference is backlog A1, and it moved DSCR 3.62x ->
 * 3.52x and IRR 10.35% -> 9.45% on a real delivered deal.
 *
 * A tenant not yet in term, or already out of it, earns NOTHING on that day - in-place NOI
 * cannot contain rent from a lease that is not running.
 */
export const annualRentRateAsOf = (input: {
  tenants: ScheduleTenant[];
  /** yyyy-mm-dd. The day the in-place NOI describes, which is TODAY on the analyzer screen. */
  asOf: string;
  /**
   * The day `monthly_base_rent` is current as of. ⛔ Deliberately NOT defaulted to `asOf`: the
   * anchor of an inferred series is a property of the DOCUMENT, so it must not move when the
   * caller asks about a different day. See `expandRecurring`.
   */
  baseRentAsOf?: string;
}): AnnualRentRateResult => {
  const when = parseDate(input.asOf);
  const notes: string[] = [];
  const warnings: string[] = [];

  if (!when) {
    warnings.push(`"${input.asOf}" is not a yyyy-mm-dd date, so no rent rate can be read from the leases.`);
    return { rate: null, notes, warnings };
  }
  if (!input.tenants.length) {
    warnings.push('No tenant rent roll on this property, so there is no contractual rent rate to anchor on.');
    return { rate: null, notes, warnings };
  }

  let total = 0;
  for (const t of input.tenants) {
    const name = t.tenant_name ?? 'Tenant';
    const ls = parseDate(t.lease_start);
    const le = parseDate(t.lease_end);

    if (ls && when.getTime() < ls.getTime()) {
      notes.push(`${name} does not take occupancy until ${t.lease_start} - no rent on ${input.asOf}.`);
      continue;
    }
    if (le && when.getTime() > le.getTime()) {
      notes.push(`${name}'s lease ended ${t.lease_end} - no rent on ${input.asOf}.`);
      continue;
    }

    const at = annualRentAt(t, when, parseDate(input.baseRentAsOf));
    if (at === null) {
      // ⛔ Refuse. A guessed anchor silently shifts every year of the schedule.
      warnings.push(`${name} has a CPI or underivable step on or before ${input.asOf}, so the current annual rent rate cannot be derived.`);
      return { rate: null, notes, warnings };
    }
    total += at.rent;
    if (at.note) notes.push(at.note);
  }

  return { rate: total, notes, warnings };
};

export interface LeaseYearRow {
  /** ISO date this rate takes effect. */
  from: string;
  /** Total annual run-rate in force from that date, across every tenant then in term. */
  annualRent: number;
  /** Which tenants stepped on this date. Empty on the opening row, which is a starting point. */
  drivers: string[];
  /** True when a driver's effective date was DERIVED from lease commencement, not quoted. */
  inferred: boolean;
}

/**
 * ⭐⭐ THE CONTRACT, NOT THE COLLECTION - the second of the two tables the screen shows.
 *
 * `buildRentSchedule` answers "what do I collect in hold year 2" and BLENDS, because a step
 * landing in March is worth ten months that year. That is Joseph's Pitfall 3, his 2026-08-01
 * ruling, and what ARGUS does - and it is the right basis for a proforma.
 *
 * It is the WRONG table to check a lease against. This one lists the rent in force from each
 * effective date, which is what an OM prints and what a reader has in front of them. Showing only
 * the blended figures is why a reader cannot reconcile the proforma against the document.
 *
 * ⛔ Rows carry `inferred` because printing a DERIVED anniversary in a table someone checks
 * against the lease, without saying so, is exactly how a guess becomes a fact.
 */
export const leaseYearSchedule = (input: {
  tenants: ScheduleTenant[];
  /** Usually the close. The opening row states the rate in force on this day. */
  from: string;
  through: string;
  /** The day `monthly_base_rent` is current as of. See `expandRecurring`. */
  baseRentAsOf?: string;
}): LeaseYearRow[] => {
  const start = parseDate(input.from);
  const end = parseDate(input.through);
  if (!start || !end || !input.tenants.length) return [];

  const rateOn = (iso: string) =>
    annualRentRateAsOf({ tenants: input.tenants, asOf: iso, baseRentAsOf: input.baseRentAsOf }).rate;

  // Every step date any tenant carries inside the window, from the EXPANDED set so a recurring
  // rule contributes each of its occurrences rather than only the one the extractor wrote down.
  const steps = new Map<string, { drivers: string[]; inferred: boolean }>();
  for (const t of input.tenants) {
    for (const e of expandRecurring(t, parseDate(input.baseRentAsOf))) {
      const on = parseDate(e.effective_date);
      if (!on || on.getTime() <= start.getTime() || on.getTime() > end.getTime()) continue;
      const iso = toIso(on);
      const entry = steps.get(iso) ?? { drivers: [], inferred: false };
      const who = t.tenant_name ?? 'Tenant';
      if (!entry.drivers.includes(who)) entry.drivers.push(who);
      if (e.date_basis === 'inferred') entry.inferred = true;
      steps.set(iso, entry);
    }
  }

  const opening = rateOn(input.from);
  if (opening === null) return [];
  const rows: LeaseYearRow[] = [{ from: input.from, annualRent: opening, drivers: [], inferred: false }];

  for (const iso of [...steps.keys()].sort()) {
    const rate = rateOn(iso);
    if (rate === null) continue;
    // A step that moves nothing - a restated current rent - is not a row a reader needs.
    if (Math.abs(rate - rows[rows.length - 1].annualRent) < 1) continue;
    rows.push({ from: iso, annualRent: rate, ...steps.get(iso)! });
  }
  return rows;
};
